import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  AttachMoney,
  TrendingUp,
  Payment,
  People,
  Schedule,
  CheckCircle
} from '@mui/icons-material';
import { api } from '../services/api';

interface WeekdaySummary {
  collected: number;
  pending: number;
  total: number;
}

interface CollectorDashboardData {
  daily_stats: {
    collected: number;
    count: number;
    pending: number;
    pending_amount: number;
  };
  weekly_stats: {
    collected: number;
    count: number;
    pending: number;
    pending_amount: number;
    target: number;
    progress: number;
  };
  monthly_stats: {
    collected: number;
    count: number;
    target: number;
    progress: number;
  };
  today_collections: Array<{
    sale_id: number;
    customer_name: string;
    customer_surname: string;
    customer_address: string;
    installment_amount: number;
    installment_number: number;
    total_installments: number;
  }>;
  week_summary: {
    monday: WeekdaySummary;
    tuesday: WeekdaySummary;
    wednesday: WeekdaySummary;
    thursday: WeekdaySummary;
    friday: WeekdaySummary;
    saturday: WeekdaySummary;
    sunday: WeekdaySummary;
  };
}

const CollectorDashboard: React.FC = () => {
  const [data, setData] = useState<CollectorDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/collector/dashboard');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const StatCard = ({ title, value, icon, color, gradient, subtitle }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    gradient: string;
    subtitle?: string;
  }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: gradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          transform: 'translate(30px, -30px)',
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box sx={{ flex: 1 }}>
            <Typography 
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 500,
                opacity: 0.9,
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                fontWeight: 700,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography 
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  opacity: 0.8,
                  mt: 0.5
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box 
            sx={{ 
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              ml: 1,
              opacity: 0.8,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ProgressCard = ({ title, progress, collected, target, color }: {
    title: string;
    progress: number;
    collected: number;
    target: number;
    color: string;
  }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h6" 
          gutterBottom
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {title}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              ${collected.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {progress.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(progress, 100)} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: 4
              }
            }} 
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Meta: ${target.toLocaleString()}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.75rem', sm: '2.125rem' },
          fontWeight: 600,
          color: '#1E293B',
          mb: { xs: 2, sm: 4 }
        }}
      >
        Dashboard del Cobrador
      </Typography>

      {/* Estadísticas del día */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cobrado Hoy"
            value={`$${data.daily_stats.collected.toLocaleString()}`}
            icon={<AttachMoney />}
            color="success.main"
            gradient="linear-gradient(135deg, #10B981, #34D399)"
            subtitle={`${data.daily_stats.count} pagos`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pendientes Hoy"
            value={data.daily_stats.pending}
            icon={<Schedule />}
            color="warning.main"
            gradient="linear-gradient(135deg, #F59E0B, #FBBF24)"
            subtitle={`$${data.daily_stats.pending_amount.toLocaleString()}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pendientes Semana"
            value={data.weekly_stats.pending}
            icon={<People />}
            color="error.main"
            gradient="linear-gradient(135deg, #EF4444, #F87171)"
            subtitle={`$${data.weekly_stats.pending_amount.toLocaleString()}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pagos Semana"
            value={data.weekly_stats.count}
            icon={<CheckCircle />}
            color="info.main"
            gradient="linear-gradient(135deg, #6366F1, #818CF8)"
            subtitle={`$${data.weekly_stats.collected.toLocaleString()}`}
          />
        </Grid>
      </Grid>

      {/* Progreso semanal y mensual */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Progreso Semanal"
            progress={data.weekly_stats.progress}
            collected={data.weekly_stats.collected}
            target={data.weekly_stats.target}
            color="#6366F1"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Progreso Mensual"
            progress={data.monthly_stats.progress}
            collected={data.monthly_stats.collected}
            target={data.monthly_stats.target}
            color="#EC4899"
          />
        </Grid>
      </Grid>

      {/* Resumen de la semana */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 4 } }}>
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                Resumen de la Semana (Cobrados/Pendientes)
              </Typography>
              <Grid container spacing={2}>
                {(Object.entries(data.week_summary) as Array<[string, WeekdaySummary]>).map(([day, summary]) => (
                  <Grid item xs={6} sm={4} md={1.7} key={day}>
                    <Box textAlign="center">
                      <Typography
                        variant="h6"
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {summary.collected}/{summary.pending}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {day === 'monday' ? 'Lun' :
                         day === 'tuesday' ? 'Mar' :
                         day === 'wednesday' ? 'Mié' :
                         day === 'thursday' ? 'Jue' :
                         day === 'friday' ? 'Vie' :
                         day === 'saturday' ? 'Sáb' : 'Dom'}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lista de cobranzas pendientes para hoy */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Cobranzas Pendientes para Hoy
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Cliente</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Dirección</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Cuota</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Monto</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.today_collections.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      ¡Excelente! No hay cobranzas pendientes para hoy.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.today_collections.map((collection) => (
                  <TableRow key={`${collection.sale_id}-${collection.installment_number}`} hover>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {collection.customer_surname} {collection.customer_name}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {collection.customer_address}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {collection.installment_number}/{collection.total_installments}
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      ${collection.installment_amount.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label="Pendiente" 
                        color="warning" 
                        size="small"
                        sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default CollectorDashboard;
